import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import * as Yup from "yup";
import {
  Button,
  useMediaQuery,
  useTheme,
  Chip,
  Box,
  Step,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useFormik } from "formik";
import AuthContext from "../../../context/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import { useEffectOnce } from "../../../utils/effects";
import {
  ensureTerminalIds,
  generateTerminalId,
} from "../../../utils/terminalId";

// Import form components
import TerminalsFields from "./MerchantAndTerminals.terminalsFields";
import AcquirerFields from "./MerchantAndTerminals.acquirerFields";
import MerchantFields from "./MerchantAndTerminals.merchantFields";
import BankFields from "./MerchantAndTerminals.bankFields";
import OwnersFields from "./MerchantAndTerminals.ownersFields";
import ResultModal from "./ResultModal";

// Import Formik resources
import {
  merchantAndTerminalsInitialValues,
  createMerchantAndTerminalsValidationSchema,
  createShortMerchantAndTerminalsValidationSchema,
  setMerchantAndTerminalsValues,
} from "./MerchantAndTerminals.resources";

// Import Redux actions
import {
  setActiveStep,
  setFilteredSteps,
  setLoading,
  setReadOnlyForm,
  setFile,
  setSuccessfulData,
  setFailedData,
  setIsModalOpen,
} from "./MerchantAndTerminals.slice";

// Import LoadEnrollment actions
import {
  SetEnrollmentSelected,
  ResetDataFilteredByid,
} from "../../lists/LoadEnrollment/LoadEnrollment.slice";

import {
  createEnrollment,
  updateEnrollment,
  processFileUpload,
  getTerminalById,
  updateTerminal,
} from "./MerchantAndTerminals.thunk";

// Import LoadEnrollment thunks
import {
  createEnrollmentLoad,
  updateEnrollmentLoad,
  getEnrollmentByIdLoad,
} from "../../lists/LoadEnrollment/LoadEnrollment.thunk";

// Import styles
import {
  Stepper as StyledStepper,
  StepLabel as StyledStepLabel,
  FileUploadContainer,
  GridContainer,
  GridItem,
  MerchantAndTerminalsFormRootArea,
} from "./MerchantAndTerminals.styles";

import AdminFields from "../../adminFields/AdminFields";
import {
  setApplyChanges,
  setWasUsed,
  setReset,
  setAcquirerCurrencyId,
} from "../../adminFields/AdminFields.slice";

const MerchantAndTerminals = ({
  goBack,
  setSnackOpen,
  setMessageOpen,
  setSeverity,
  setSubmenuMerchantTerminal,
  setSelectedOption,
  setNameSection,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { userInfo, token, acquirerData } = useContext(AuthContext);

  // Redux state
  const {
    formType,
    initialFormData,
    enrollmentId,
    activeStep,
    filteredSteps,
    loading,
    readOnlyForm,
    file,
    successfulData,
    failedData,
    isModalOpen,
    terminalItemInfo,
    statusTerminalRetrieve,
    terminalData,
  } = useSelector((state) => state.merchantAndTerminals);

  // LoadEnrollment state
  const { enrollmentSelected, enrollmentDataById, statusRetrieve } =
    useSelector((state) => state.loadEnrollment);

  const [role, setRole] = useState(null);

  const handleGoMerchantArea = () => {
    setSubmenuMerchantTerminal("merchant_area");
    setSelectedOption("merchant_area");
    setNameSection("Merchant & Terminal - Merchant");
  };

  const {
    applyChanges,
    acquirerId,
    isoID: storeIsoID,
    wasUsed,
    reset,
    acquirerCurrencyId,
  } = useSelector((state) => state.adminFields);

  const calculateValidationSchema = useCallback(
    (acquirerCurrencyId) => {
      if (filteredSteps.length === 2)
        return createShortMerchantAndTerminalsValidationSchema(formType);
      return createMerchantAndTerminalsValidationSchema(
        formType,
        acquirerCurrencyId
      );
    },
    [filteredSteps]
  );

  // Formik hook
  const {
    handleChange,
    handleBlur,
    handleSubmit: formikHandleSubmit,
    values,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
    isSubmitting,
  } = useFormik({
    initialValues: initialFormData || merchantAndTerminalsInitialValues,
    // validationSchema: getValidationSchema,
    validationSchema: calculateValidationSchema(acquirerCurrencyId),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (activeStep < stepsToDisplay.length - 1) return;
      setSubmitting(true);
      try {
        if (formType === "editTerminal") await handleSubmitFormEdit(values);
        if (formType === "createEnrollment") await handleSubmitForm(values);
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // UseEffect to set acquirer data in the form
  // execute only when parent component is mounted
  useEffect(() => {
    if (acquirerData?.isoID) {
      handleChange({ target: { name: "acquirer.iso", value: "True" } });
      handleChange({
        target: { name: "acquirer.isoID", value: acquirerData.isoID },
      });
    } else {
      handleChange({ target: { name: "acquirer.iso", value: "False" } });
    }
    if (acquirerData?.currency_code) {
      handleChange({
        target: {
          name: "acquirer.currency",
          value: acquirerData.currency_code,
        },
      });
    }
    handleChange({ target: { name: "acquirer.amountOther", value: "False" } });
    handleChange({ target: { name: "acquirer.store", value: "0001" } });
  }, [acquirerData, handleChange]);

  // Admin fields logic start ===============================
  useEffect(() => {
    if (reset) {
      setFieldValue("acquirer.isoID", acquirerData?.isoID);
      setFieldValue("acquirer.currency", acquirerData?.currency);
      dispatch(setReset(false));
    }
  }, [reset]);

  useEffect(() => {
    if (applyChanges) {
      setFieldValue("acquirer.isoID", storeIsoID);
      if (storeIsoID === "") setFieldValue("acquirer.iso", "False");
      if (storeIsoID !== "") setFieldValue("acquirer.iso", "True");

      setFieldValue("acquirer.currency", acquirerCurrencyId);

      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(true));
    }
  }, [applyChanges]);

  useEffect(() => {
    if (wasUsed && storeIsoID === "" && acquirerId === "") {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(false));
      setFieldValue("acquirer.isoID", acquirerData?.isoID);
      setFieldValue("acquirer.currency", acquirerData?.currency);
    }
  }, [storeIsoID, acquirerId]);

  // Admin fields logic end ===============================

  // Initialize form based on formType
  useEffect(() => {
    // Set readOnly for seeTerminal mode
    if (formType === "seeTerminal") {
      dispatch(setReadOnlyForm(true));
    } else {
      dispatch(setReadOnlyForm(false));
    }

    // Set enrollment selected for loadEnrollment mode
    if (enrollmentId) {
      dispatch(SetEnrollmentSelected({ enrollmentSelected: enrollmentId }));
    }
  }, [formType, enrollmentId, dispatch]);

  // Set role from user info
  useEffect(() => {
    if (userInfo) {
      setRole(userInfo["acquirer-rol-data"]);
    }
  }, [userInfo]);

  // Load enrollment data for loadEnrollment mode
  useEffectOnce(async () => {
    if (enrollmentSelected) {
      dispatch(getEnrollmentByIdLoad({ token, id: enrollmentSelected }));
    }
  });

  // Handle enrollment data loaded
  useEffect(() => {
    if (
      enrollmentSelected &&
      enrollmentDataById &&
      statusRetrieve === "succeeded"
    ) {
      const { data, current_step } = enrollmentDataById;
      setMerchantAndTerminalsValues({
        formData: data,
        setFieldValue,
        formType,
      });
      dispatch(
        setActiveStep(current_step > 0 ? current_step - 1 : current_step)
      );
      dispatch(SetEnrollmentSelected({ enrollmentSelected: null }));
    }
  }, [
    enrollmentDataById,
    enrollmentSelected,
    formType,
    statusRetrieve,
    dispatch,
  ]);

  // Initialize filtered steps on component mount
  useEffect(() => {
    if (!filteredSteps || filteredSteps.length === 0) {
      const defaultSteps = [
        "Merchant Info",
        "Merchant Commissions",
        "Bank Info",
        "Owners Info",
        "Terminals",
      ];
      dispatch(setFilteredSteps(defaultSteps));
    }
  }, [filteredSteps.length, dispatch]);

  // Update filtered steps based on ISO selection
  useEffect(() => {
    if (values?.acquirer?.iso && filteredSteps.length > 0) {
      const newFilteredSteps =
        values.acquirer.iso === "True"
          ? [
              "Merchant Info",
              "Merchant Commissions",
              "Bank Info",
              "Owners Info",
              "Terminals",
            ]
          : ["Merchant Info", "Terminals"];

      // Only update if the steps are actually different
      if (JSON.stringify(newFilteredSteps) !== JSON.stringify(filteredSteps)) {
        dispatch(setFilteredSteps(newFilteredSteps));
      }
    }
  }, [values?.acquirer?.iso, filteredSteps, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(ResetDataFilteredByid());
    };
  }, [dispatch]);

  // Manejar errores de ResizeObserver
  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        // Ignorar este error específico
        e.stopImmediatePropagation();
        return false;
      }
    };

    window.addEventListener("error", resizeObserverError);

    return () => {
      window.removeEventListener("error", resizeObserverError);
    };
  }, []);

  // Evita múltiples fetches del mismo terminal (Strict Mode monta 2x, o re-renders con ref nueva)
  const fetchedTerminalKeyRef = useRef(null);
  const hadTerminalDataRef = useRef(false);

  useEffect(() => {
    if (!terminalItemInfo || !token) return;
    if (statusTerminalRetrieve === "loading") return;

    const key = `${terminalItemInfo.merchantID ?? ""}-${terminalItemInfo.terminalID ?? ""}`;

    if (terminalData) {
      hadTerminalDataRef.current = true;
      return;
    }
    // Si teníamos data y ahora no (usuario volvió a abrir el mismo u otro terminal), permitir fetch de nuevo
    if (hadTerminalDataRef.current) {
      hadTerminalDataRef.current = false;
      fetchedTerminalKeyRef.current = null;
    }
    if (fetchedTerminalKeyRef.current === key) return;

    fetchedTerminalKeyRef.current = key;
    const { merchantID, acquirerData } = terminalItemInfo;
    dispatch(
      getTerminalById({
        token,
        merchantID,
        terminalID: "getAllTerminals",
        acquirerData,
      })
    );
  }, [
    terminalItemInfo,
    statusTerminalRetrieve,
    terminalData,
    dispatch,
    token,
  ]);

  // Función para mapear terminalData al formulario
  const mapTerminalDataToForm = useCallback(
    (terminalData) => {
      if (!terminalData) return;
      try {
        const { acquirer, fiat, bank, merchant, owner, crypto_wallets } =
          terminalData;

        // Mapear datos del acquirer
        if (acquirer) {
          setFieldValue("acquirer.iso", acquirer.iso ? "True" : "False");
          setFieldValue("acquirer.isoID", acquirer.isoID || "");
          // Keep Redux currency in sync with form so Bank Info Yup/step validation
          // matches UI (e.g. MXN hides routing_number but still validated it before).
          const currencyCode = fiat?.currencyCode || "";
          setFieldValue("acquirer.currency", currencyCode);
          dispatch(setAcquirerCurrencyId(currencyCode));
          setFieldValue("acquirer.holding", acquirer.merchantHolding || "");
          setFieldValue("acquirer.holdingId", acquirer.merchantHoldingID || "");
          setFieldValue("acquirer.group", acquirer.merchantGroup || "");
          setFieldValue("acquirer.groupId", acquirer.merchantGroupID || "");
          setFieldValue("acquirer.name", acquirer.merchantName || "");
          setFieldValue("acquirer.store", acquirer.merchantStore || "");
          setFieldValue("acquirer.address", acquirer.merchantAddress || "");
          setFieldValue(
            "acquirer.neighborhood",
            acquirer.merchantNeighborhood || ""
          );
          setFieldValue("acquirer.city", acquirer.merchantCity || "");
          setFieldValue("acquirer.merchantState", acquirer.merchantState || "");
          setFieldValue(
            "acquirer.postalCode",
            acquirer.merchantPostalCode || ""
          );
          setFieldValue("acquirer.mccID", acquirer.mccID || "");
          setFieldValue("acquirer.merchantID", acquirer.merchantID || "");
          setFieldValue(
            "acquirer.surcharge",
            acquirer.surcharge != null && acquirer.surcharge !== ""
              ? String(acquirer.surcharge)
              : "0"
          );
          // No mapear email en editTerminal ya que no se muestra ni valida

          // Mapear terminales
          if (acquirer.terminal?.terminals) {
            setFieldValue(
              "acquirer.terminal.terminals",
              acquirer.terminal.terminals
            );
          }
        }

        // Mapear datos del merchant
        if (merchant) {
          setFieldValue("merchant.legal_name", merchant.legalName || "");
          setFieldValue(
            "merchant.business_email",
            merchant.businessEmail || ""
          );
          setFieldValue(
            "merchant.business_phone",
            merchant.businessPhone || ""
          );
          setFieldValue("merchant.business_type", merchant.businessType || "");
          setFieldValue("merchant.taxpayer_id", merchant.taxpayerID || "");
          setFieldValue(
            "merchant.incorporation_date",
            merchant.incorporationDate || ""
          );
          setFieldValue(
            "merchant.effective_date",
            merchant.effectiveDate || ""
          );
          setFieldValue(
            "merchant.amount_tx_processing",
            merchant.amountTxProcessing != null && merchant.amountTxProcessing !== ""
              ? String(merchant.amountTxProcessing)
              : ""
          );
          setFieldValue(
            "merchant.amount_tx_processing_tiers",
            merchant.amountTxProcessingTiers || "0"
          );
          setFieldValue(
            "merchant.percent_tx_processing",
            merchant.percentTxProcessing != null && merchant.percentTxProcessing !== ""
              ? String(merchant.percentTxProcessing)
              : ""
          );
          setFieldValue(
            "merchant.percent_tx_processing_tiers",
            merchant.percentTxProcessingTiers || "0"
          );
          setFieldValue(
            "merchant.amount_terminal_maintenance",
            merchant.amountTerminalMaintenance || "0"
          );
          setFieldValue(
            "merchant.amount_terminal_maintenance_tiers",
            merchant.amountTerminalMaintenanceTiers || "0"
          );
          setFieldValue(
            "merchant.terminal_maintenance_inactive",
            merchant.terminalMaintenanceInactive ? "True" : "False"
          );
          setFieldValue(
            "merchant.amount_terminal_setup",
            merchant.amountTerminalSetup || "0"
          );
          setFieldValue(
            "merchant.amount_terminal_setup_tiers",
            merchant.amountTerminalSetupTiers || "0"
          );
        }

        // Mapear datos del bank
        if (bank) {
          setFieldValue("bank.Bid", bank.Bid || "");
          setFieldValue("bank.swift_code", bank.swiftCode || "");
          setFieldValue("bank.account_number", bank.accountNumber || "");
          setFieldValue("bank.address", bank.address || "");
          setFieldValue("bank.routing_number", bank.routingNumber || "");
          setFieldValue("bank.Iban", bank.Iban || "");
          setFieldValue("bank.branch_number", bank.branchNumber || "");
          setFieldValue("bank.city", bank.city || "");
          setFieldValue("bank.state", bank.state || "");
          setFieldValue("bank.postal_code", bank.postalCode || "");
          setFieldValue("bank.country_code", ""); // No viene en la data del terminal
        }

        if (crypto_wallets) {
          setFieldValue("crypto_wallets", crypto_wallets);
        }

        // Mapear datos del owner
        if (owner && owner.length > 0) {
          const mappedOwners = owner.map((ownerData) => ({
            first_name: ownerData.firstName || "",
            last_name: ownerData.lastName || "",
            middle_name: ownerData.middleName || "",
            id: ownerData.ID || "",
            taxpayer_id: ownerData.taxpayerID || "",
            percentage: ownerData.percentage || "0",
            id_issuer: ownerData.idIssuer || "",
            personal_email: ownerData.personalEmail || "",
            personal_phone: ownerData.personalPhone || "",
            address: ownerData.address || "",
            city: ownerData.city || "",
            state: ownerData.state || "",
            country: ownerData.country || "",
            postal_code: ownerData.postalCode || "",
            neighborhood: ownerData.neighborhood || "",
            residency_time: ownerData.residencyTime || "00",
          }));

          setFieldValue("owner", mappedOwners);
        }
      } catch (error) {
        console.error("❌ Error mapeando terminalData:", error);
      }
    },
    [setFieldValue, dispatch]
  );

  // Mapear terminalData al formulario cuando esté disponible
  useEffect(() => {
    if (terminalData && Object.keys(terminalData).length > 0) {
      mapTerminalDataToForm(terminalData);
    }
  }, [terminalData, mapTerminalDataToForm]);

  const validateCurrentStep = async () => {
    try {
      // Define which fields belong to each step (similar to AcquirerForm)
      const stepFields = {
        "Merchant Info": [
          // "acquirer.iso",
          // "acquirer.currency",
          "acquirer.name",
          "acquirer.address",
          "acquirer.merchantID",
          "acquirer.mccID",
          "acquirer.city",
          "acquirer.merchantState",
          "acquirer.postalCode",
          "merchant.business_email",
          ...(acquirerData?.isoID
            ? [
                "merchant.legal_name",
                "merchant.business_phone",
                "merchant.business_type",
                "merchant.taxpayer_id",
              ]
            : []),
          // Campos condicionales basados en formType
          ...(formType === "editTerminal" || formType === "seeTerminal"
            ? ["acquirer.holding", "acquirer.holdingId"]
            : []),
          // Campo email solo si no es editTerminal
          ...(formType !== "editTerminal" || formType !== "seeTerminal"
            ? ["acquirer.email"]
            : []),
        ],
        "Merchant Commissions": [
          "merchant.amount_tx_processing",
          "merchant.percent_tx_processing",
          "acquirer.surcharge",
        ],
        "Bank Info": [
          "bank.Iban",
          "bank.address",
          "bank.account_number",
          ...(acquirerCurrencyId !== "MXN" ? ["bank.routing_number"] : []),
        ],
        "Owners Info": ["owner"],
        Terminals: ["acquirer.terminal.terminals"],
      };

      // Get current step name
      const currentStepName = stepsToDisplay[activeStep];
      const currentStepFields = stepFields[currentStepName] || [];

      // Mark all current step fields as touched first
      await Promise.all(
        currentStepFields.map(async (fieldPath) => {
          setFieldTouched(fieldPath, true, false);
        })
      );

      // Get validation errors for all fields using Yup
      const validationErrors = await validateForm();

      // Filter errors to only include current step fields
      const currentStepErrors = {};

      for (const fieldPath of currentStepFields) {
        const pathParts = fieldPath.split(".");
        let errorValue = validationErrors;

        for (const part of pathParts) {
          errorValue = errorValue?.[part];
          if (!errorValue) break;
        }

        if (errorValue) {
          // Build the error path
          let currentError = currentStepErrors;
          for (let i = 0; i < pathParts.length - 1; i++) {
            currentError[pathParts[i]] = currentError[pathParts[i]] || {};
            currentError = currentError[pathParts[i]];
          }
          currentError[pathParts[pathParts.length - 1]] = errorValue;
        }
      }

      // Special handling for owner array - ensure it's marked as touched
      if (currentStepFields.includes("owner")) {
        setFieldTouched("owner", true, false);
      }

      if (Object.keys(currentStepErrors).length > 0) {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Validation error:", error);
      return false;
    }
  };

  const handleNext = async () => {
    if (formType !== "seeTerminal") {
      const isStepValid = await validateCurrentStep();
      if (!isStepValid) {
        const errorMessage = "Please fill in all required fields!";
        setSeverity("error");
        setMessageOpen(errorMessage);
        setSnackOpen(true);
        return;
      }
    }

    const nextStep =
      activeStep < stepsToDisplay.length - 1 ? activeStep + 1 : activeStep;

    // Handle different form types
    if (formType === "createEnrollment") {
      if (!enrollmentDataById) {
        dispatch(
          createEnrollmentLoad({
            token,
            formData: values,
            userMail: userInfo?.email || "",
            currentStep: nextStep,
            enrollment_type: !acquirerData?.isoID
              ? "MERCHANT_ACQUIRER"
              : "MERCHANT_ISO",
          })
        );
      } else {
        dispatch(
          updateEnrollmentLoad({
            token,
            formData: values,
            userMail: userInfo?.email || "",
            enrollmentId: enrollmentDataById,
            currentStep: nextStep,
            enrollment_type: !acquirerData?.isoID
              ? "MERCHANT_ACQUIRER"
              : "MERCHANT_ISO",
          })
        );
      }
    }

    dispatch(
      setActiveStep(
        activeStep < stepsToDisplay.length - 1 ? activeStep + 1 : activeStep
      )
    );
  };

  const handleBack = () => {
    dispatch(setActiveStep(activeStep > 0 ? activeStep - 1 : activeStep));
  };

  const handleTerminalChange = (index, field, value) => {
    if (field === "terminalType") {
      const hasEcommerce = values.acquirer.terminal.terminals.some(
        (t, i) => i !== index && t.terminalType === "E"
      );

      if (value === "E" && hasEcommerce) {
        let errorMessage =
          "Only one eCommerce type terminal is allowed per merchant!";
        setSeverity("error");
        setMessageOpen(errorMessage);
        setSnackOpen(true);
        return;
      }
    }

    setFieldValue(`acquirer.terminal.terminals.${index}.${field}`, value);

    if (
      field === "terminalType" &&
      value &&
      formType !== "editTerminal" &&
      formType !== "seeTerminal"
    ) {
      const currentTerminal = values.acquirer.terminal.terminals[index];
      if (!currentTerminal.terminalID) {
        setFieldValue(
          `acquirer.terminal.terminals.${index}.terminalID`,
          generateTerminalId()
        );
      }
    }
  };

  const handleAddTerminal = (initialValues = {}) => {
    const currentTerminals = values.acquirer.terminal.terminals;
    const newTerminal = {
      terminalType: initialValues.terminalType || "",
      terminalID: initialValues.terminalID || generateTerminalId(),
      terminalSerial: initialValues.terminalSerial || "",
    };
    setFieldValue("acquirer.terminal.terminals", [
      ...currentTerminals,
      newTerminal,
    ]);
  };

  const handleDeleteTerminal = (index) => {
    const currentTerminals = values.acquirer.terminal.terminals;
    const updatedTerminals = currentTerminals.filter((_, i) => i !== index);
    setFieldValue("acquirer.terminal.terminals", updatedTerminals);
  };

  const handleAddOwner = () => {
    const currentOwners = values.owner;
    const newOwner = {
      first_name: "",
      last_name: "",
      middle_name: "",
      id: "",
      taxpayer_id: "",
      percentage: "",
      id_issuer: "",
      personal_email: "",
      personal_phone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
      neighborhood: "",
      residency_time: "",
    };

    // Usar setTimeout para evitar conflictos con ResizeObserver
    setTimeout(() => {
      setFieldValue("owner", [...currentOwners, newOwner]);
    }, 0);
  };

  const handleOwnerChange = (index, field, value) => {
    setFieldValue(`owner.${index}.${field}`, value);
  };

  const handleDeleteOwner = (index) => {
    const currentOwners = values.owner;
    const updatedOwners = currentOwners.filter((_, i) => i !== index);

    // Usar setTimeout para evitar conflictos con ResizeObserver
    setTimeout(() => {
      setFieldValue("owner", updatedOwners);
    }, 0);
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      dispatch(setFile(uploadedFile));
    }
  };

  const handleProcessFileUpload = () => {
    if (file) {
      dispatch(processFileUpload({ file, token, acquirerData })).then(
        (action) => {
          if (action.meta.requestStatus === "fulfilled") {
            const { successfulData, failedData } = action.payload;
            dispatch(setSuccessfulData(successfulData));
            dispatch(setFailedData(failedData));
            dispatch(setIsModalOpen(true));
          } else {
            dispatch(
              setFailedData(
                action.payload || { message: "Error processing file" }
              )
            );
            dispatch(setIsModalOpen(true));
          }
        }
      );
    } else {
      dispatch(setFailedData("No files selected for upload."));
      dispatch(setIsModalOpen(true));
    }
  };

  const handleSubmitFormEdit = async (formValues = values) => {
    try {
      await dispatch(
        updateTerminal({
          formData: formValues,
          token,
          acquirerData,
          onSuccessCallback: handleGoMerchantArea,
        })
      ).unwrap();
    } catch (error) {
      let errorMessage = "Error updating terminal";
      if (error.message.includes("TERMINAL_SERIAL_ALREADY_EXISTS")) {
        errorMessage =
          "Terminal Serial already exists. Please use a different Terminal Serial.";
      }
      setSeverity("error");
      setMessageOpen(errorMessage);
      setSnackOpen(true);
    }
  };

  const handleSubmitForm = async (formValues = values) => {
    const terminalsWithIds = ensureTerminalIds(
      formValues.acquirer.terminal.terminals
    );

    if (
      terminalsWithIds.some(
        (terminal) =>
          !terminal.terminalID ||
          !terminal.terminalType ||
          !terminal.terminalSerial
      )
    ) {
      const errorMessage =
        "Please fill in all terminal information before submitting!";
      setSeverity("error");
      setMessageOpen(errorMessage);
      setSnackOpen(true);
      return;
    }

    const submissionValues = {
      ...formValues,
      acquirer: {
        ...formValues.acquirer,
        terminal: {
          ...formValues.acquirer.terminal,
          terminals: terminalsWithIds,
        },
      },
    };

    try {
      dispatch(setLoading(true));

      let successMessage = "";

      if (formType === "createEnrollment") {
        await dispatch(
          createEnrollment({
            formData: submissionValues,
            token,
            acquirerData: {
              ...acquirerData,
              isoID: submissionValues.acquirer.isoID,
              currency_code: submissionValues.acquirer.currency,
              ...(wasUsed && { acquirerId: acquirerId }),
            },
            userInfo,
            onSuccessCallback: handleGoMerchantArea,
          })
        ).unwrap();
        successMessage = "Merchant Terminal successfully saved!";
      }
      // else if (formType === "editTerminal") {
      //   await dispatch(
      //     updateEnrollment({
      //       formData: submissionValues,
      //       token,
      //       acquirerData,
      //       enrollmentId,
      //     })
      //   ).unwrap();
      //   successMessage = "Merchant Terminal successfully updated!";
      // }

      if (enrollmentDataById) {
        await dispatch(
          updateEnrollmentLoad({
            token,
            formData: submissionValues,
            userMail: userInfo?.email || "",
            enrollmentId: enrollmentDataById,
            currentStep: activeStep,
            enrollment_completed: true,
            enrollment_type: !acquirerData?.isoID
              ? "MERCHANT_ACQUIRER"
              : "MERCHANT_ISO",
          })
        ).unwrap();
      }
      successMessage = "Enrollment load successfully completed!";

      setSnackOpen(true);
      setMessageOpen(successMessage);
      setSeverity("success");
      resetForm();
      goBack();
    } catch (error) {
      const errorMessage = error.message || "Error saving data!";
      setSeverity("error");
      setMessageOpen(errorMessage);
      setSnackOpen(true);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCloseModal = () => {
    dispatch(setIsModalOpen(false));
  };

  const resetForm = () => {
    // Reset form to initial values
    Object.keys(merchantAndTerminalsInitialValues).forEach((key) => {
      if (key === "owner") {
        setFieldValue("owner", [merchantAndTerminalsInitialValues.owner[0]]);
      } else if (key === "acquirer") {
        Object.keys(merchantAndTerminalsInitialValues.acquirer).forEach(
          (acquirerKey) => {
            if (acquirerKey === "terminal") {
              setFieldValue(
                "acquirer.terminal.terminals",
                merchantAndTerminalsInitialValues.acquirer.terminal.terminals
              );
            } else {
              setFieldValue(
                `acquirer.${acquirerKey}`,
                merchantAndTerminalsInitialValues.acquirer[acquirerKey]
              );
            }
          }
        );
      } else {
        Object.keys(merchantAndTerminalsInitialValues[key]).forEach(
          (subKey) => {
            setFieldValue(
              `${key}.${subKey}`,
              merchantAndTerminalsInitialValues[key][subKey]
            );
          }
        );
      }
    });

    dispatch(setActiveStep(0));
    dispatch(setFile(null));
    dispatch(setSuccessfulData([]));
    dispatch(setFailedData([]));
  };

  const handleStepClick = async (index) => {
    if (index < activeStep) {
      dispatch(setActiveStep(index));
      return;
    }

    if (formType !== "seeTerminal") {
      const isStepValid = await validateCurrentStep();
      if (!isStepValid) {
        const errorMessage = "Please fill in all required fields!";
        setSeverity("error");
        setMessageOpen(errorMessage);
        setSnackOpen(true);
        return;
      }
    }

    dispatch(setActiveStep(index));
  };

  const hasAccess = (allowedRoles) => allowedRoles.includes(role);

  // Ensure we have steps to display
  const stepsToDisplay =
    filteredSteps && filteredSteps.length > 0
      ? filteredSteps
      : [
          "Merchant Info",
          "Merchant Commissions",
          "Bank Info",
          "Owners Info",
          "Terminals",
        ];

  if (!values) {
    return <CircularProgress />;
  }

  return (
    <MerchantAndTerminalsFormRootArea>
      <Backdrop open={loading} sx={{ color: "common.white", zIndex: 1301 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {formType === "createEnrollment" && <AdminFields />}

      <GridItem xs={12} padding="20px 0 0">
        {statusRetrieve === "loading" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <StyledStepper activeStep={activeStep} alternativeLabel>
          {stepsToDisplay.map((label, index) => (
            <Step key={index}>
              <StyledStepLabel
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: "pointer",
                  "& .MuiStepLabel-label": {
                    color:
                      activeStep === index ? "primary.main" : "text.secondary",
                    fontWeight: activeStep === index ? 700 : 400,
                  },
                }}
              >
                {label}
              </StyledStepLabel>
            </Step>
          ))}
        </StyledStepper>
      </GridItem>

      <ResultModal
        open={isModalOpen}
        onClose={handleCloseModal}
        successfulData={successfulData}
        failedData={failedData}
      />

      <GridContainer
        // component="form"
        // onSubmit={formikHandleSubmit}
        xs={12}
        padding="10px 0 0"
      >
        {activeStep === stepsToDisplay.indexOf("Merchant Info") && (
          <AcquirerFields
            formValues={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            acquirerData={acquirerData}
            errors={errors}
            touched={touched}
            readOnly={readOnlyForm}
            formType={formType}
          />
        )}
        {activeStep === stepsToDisplay.indexOf("Merchant Commissions") && (
          <MerchantFields
            formValues={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            readOnly={readOnlyForm}
          />
        )}
        {activeStep === stepsToDisplay.indexOf("Bank Info") && (
          <BankFields
            formValues={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            readOnly={readOnlyForm}
          />
        )}
        {activeStep === stepsToDisplay.indexOf("Owners Info") && (
          <GridContainer>
            <GridItem xs={12} padding="15px 0">
              <Box>
                <Chip
                  label={`${values.owner.length} Owner(s)`}
                  color="info"
                  size="small"
                />
              </Box>
            </GridItem>
            <GridItem xs={12}>
              <OwnersFields
                formValues={values}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleOwnerChange={handleOwnerChange}
                handleDeleteOwner={handleDeleteOwner}
                errors={errors}
                touched={touched}
                readOnly={readOnlyForm}
              />
            </GridItem>
            <GridItem xs={12} padding="15px 0">
              <Box display="flex" alignItems="center">
                <Tooltip title="Add New Owner">
                  <Button
                    variant="contained"
                    component="span"
                    onClick={handleAddOwner}
                    disabled={readOnlyForm}
                  >
                    Add Owner
                  </Button>
                </Tooltip>
              </Box>
            </GridItem>
          </GridContainer>
        )}
        {activeStep === stepsToDisplay.indexOf("Terminals") && (
          <TerminalsFields
            formValues={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleTerminalChange={handleTerminalChange}
            handleDeleteTerminal={handleDeleteTerminal}
            handleAddTerminal={handleAddTerminal}
            errors={errors}
            touched={touched}
            setSeverity={setSeverity}
            setMessageOpen={setMessageOpen}
            setSnackOpen={setSnackOpen}
            readOnly={readOnlyForm}
          />
        )}
        <GridItem xs={12}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                dispatch(setActiveStep(stepsToDisplay.indexOf("Terminals")));
                goBack();
              }}
              size="small"
              sx={{ marginRight: 1 }}
            >
              Cancel
            </Button>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            {activeStep === stepsToDisplay.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={readOnlyForm || isSubmitting}
                onClick={() => {
                  // Solo ejecutar submit si estamos en el último paso
                  if (activeStep === stepsToDisplay.length - 1) {
                    formikHandleSubmit();
                  }
                }}
              >
                {formType === "createEnrollment"
                  ? "Finish"
                  : formType === "editTerminal"
                  ? "Update"
                  : "View"}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </GridItem>
        {formType !== "seeTerminal" && (
          <FileUploadContainer>
            <input
              accept=".xls, .xlsx"
              style={{ display: "none" }}
              id="contained-button-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="contained-button-file">
              <Button variant="contained" component="span">
                Upload Excel File
              </Button>
            </label>

            {file && (
              <Button
                variant="contained"
                color="success"
                onClick={handleProcessFileUpload}
              >
                Process Upload
              </Button>
            )}

            {hasAccess(["admin", "ISO"]) && (
              <a
                id="template"
                href={`${process.env.PUBLIC_URL}/Merchant & Terminal ISO.xlsx`}
                download
                style={{ textDecoration: "none" }}
              >
                <Button variant="outlined">Download Template ISO</Button>
              </a>
            )}

            {hasAccess(["admin", "merchant"]) && (
              <a
                id="template"
                href={`${process.env.PUBLIC_URL}/Merchant & Terminal.xlsx`}
                download
                style={{ textDecoration: "none" }}
              >
                <Button variant="outlined">Download Template</Button>
              </a>
            )}
          </FileUploadContainer>
        )}
      </GridContainer>
    </MerchantAndTerminalsFormRootArea>
  );
};

export default MerchantAndTerminals;
